import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import {
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaWhatsapp,
  FaClock,
} from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut", delay: i * 0.08 },
  }),
};

function Contact() {
  const formRef = useRef();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const sendEmail = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");

    emailjs
      .sendForm(
        "service_59ypico",         // ✅ your IDs kept as-is
        "template_0ucx7i1",
        formRef.current,
        "uM_t7VYCV-sNxWD_1"
      )
      .then(
        () => {
          setSent(true);
          setLoading(false);
          formRef.current.reset();
          setTimeout(() => setSent(false), 6000);
        },
        (error) => {
          console.error("FAILED...", error?.text || error);
          setErrorMsg("Something went wrong. Please try again.");
          setLoading(false);
        }
      );
  };

  return (
    <div className="relative min-h-screen pt-28 pb-20">
      {/* Soft gradient background + blobs */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-blue-50 via-white to-white" />
      <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-blue-200 opacity-30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-cyan-200 opacity-30 blur-3xl" />

      {/* Header */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="text-center max-w-3xl mx-auto px-4"
      >
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-blue-800">
          Get in Touch
        </h1>
        <p className="mt-3 text-lg text-gray-600">
          Questions, admissions, or visa help—send us a message and we’ll get back quickly.
        </p>
      </motion.div>

      {/* Quick info chips */}
      <div className="mt-8 px-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mx-auto grid max-w-5xl grid-cols-1 gap-3 sm:grid-cols-3"
        >
          <div className="rounded-2xl bg-white/70 backdrop-blur shadow-sm ring-1 ring-gray-100 p-3 flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white">
              <FaPhoneAlt />
            </span>
            <div>
              <p className="text-xs text-gray-500">Call us</p>
              <a href="tel:+61405141810" className="font-semibold text-gray-800 hover:underline">
                +61403883559
              </a>
            </div>
          </div>

          <a
            href="https://wa.me/61403883559"
            target="_blank"
            rel="noreferrer"
            className="rounded-2xl bg-white/70 backdrop-blur shadow-sm ring-1 ring-gray-100 p-3 flex items-center gap-3 hover:shadow-md transition"
          >
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-green-600 text-white">
              <FaWhatsapp />
            </span>
            <div>
              <p className="text-xs text-gray-500">WhatsApp</p>
              <p className="font-semibold text-gray-800">Chat now</p>
            </div>
          </a>

          <div className="rounded-2xl bg-white/70 backdrop-blur shadow-sm ring-1 ring-gray-100 p-3 flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white">
              <FaClock />
            </span>
            <div>
              <p className="text-xs text-gray-500">Hours</p>
              <p className="font-semibold text-gray-800">Mon–Fri · 9:00–5:00</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Main grid */}
      <div className="mx-auto mt-10 grid max-w-6xl grid-cols-1 gap-8 px-4 md:grid-cols-2">
        {/* Form card */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="relative rounded-3xl bg-white/80 backdrop-blur-md shadow-xl ring-1 ring-gray-200 overflow-hidden"
        >
          {/* top accent */}
          <div className="h-1.5 w-full bg-gradient-to-r from-blue-600 via-cyan-500 to-indigo-600" />
          <div className="p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-gray-800">Send us a message</h2>
            <p className="mt-1 text-gray-500">
              We usually respond within the same business day.
            </p>

            {sent && (
              <div className="mt-4 rounded-lg bg-green-50 px-4 py-3 text-green-800 ring-1 ring-green-200">
                ✅ Your message has been submitted successfully!
              </div>
            )}
            {!!errorMsg && (
              <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-red-800 ring-1 ring-red-200">
                {errorMsg}
              </div>
            )}

            <form ref={formRef} onSubmit={sendEmail} className="mt-6 space-y-5">
              {/* Honeypot (basic spam guard) */}
              <input
                type="text"
                name="website"
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
              />

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Full Name
                </label>
                <input
                  type="text"
                  name="user_name"
                  required
                  placeholder="Your name"
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 shadow-sm outline-none transition focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    name="user_email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 shadow-sm outline-none transition focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Phone (optional)
                  </label>
                  <input
                    type="tel"
                    name="user_phone"
                    placeholder="+61 ..."
                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 shadow-sm outline-none transition focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Message
                </label>
                <textarea
                  name="message"
                  rows={5}
                  required
                  placeholder="Tell us how we can help..."
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 shadow-sm outline-none transition focus:ring-4 focus:ring-blue-100 focus:border-blue-500"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="group relative inline-flex w-full items-center justify-center overflow-hidden rounded-xl bg-blue-700 px-6 py-3 font-semibold text-white transition hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:opacity-70"
              >
                <span className="absolute inset-0 -z-10 bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-600 opacity-0 transition group-hover:opacity-100" />
                {loading ? "Sending..." : "Send Message"}
              </button>

              <p className="text-xs text-gray-500">
                By submitting, you agree to be contacted by Glide Education & Visa Services.
              </p>
            </form>
          </div>
        </motion.div>

        {/* Offices & maps */}
        <div className="space-y-8">
          {/* Nepal */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="rounded-3xl bg-white/80 backdrop-blur-md shadow-xl ring-1 ring-gray-200 overflow-hidden"
          >
            <div className="h-1.5 w-full bg-gradient-to-r from-rose-500 via-pink-500 to-amber-500" />
            <div className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <span className="mt-1 text-blue-800">
                  <FaMapMarkerAlt className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">🇳🇵 Nepal Office</h3>
                  <p className="mt-1 text-gray-600">Ghyasang Marg (Behind Old Buspark), Bagbazar, Kathmandu</p>
                  <div className="mt-3 flex items-center gap-3 text-gray-700">
                    <FaPhoneAlt />
                    <a href="tel:+9779847606311" className="hover:underline">
                      +977 9847606311
                    </a>
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-gray-700">
                    <FaEnvelope />
                    <a href="mailto:info@glideedu.com.au" className="hover:underline">
                      info@glideedu.com.au
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-5 overflow-hidden rounded-2xl ring-1 ring-gray-200">
                <div className="relative aspect-video w-full">
                  <iframe
                    title="Nepal Office Map"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3532.006150639089!2d85.32164907538463!3d27.710090225703754!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39eb19006d9abebd%3A0xa2e465d12cb551fb!2sGlide%20Nepal%20Travel!5e0!3m2!1sen!2sau!4v1699250941000!5m2!1sen!2sau"
                    className="absolute inset-0 h-full w-full"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Adelaide */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="rounded-3xl bg-white/80 backdrop-blur-md shadow-xl ring-1 ring-gray-200 overflow-hidden"
          >
            <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />
            <div className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <span className="mt-1 text-blue-800">
                  <FaMapMarkerAlt className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">🇦🇺 Australia Office</h3>
                  <p className="mt-1 text-gray-600">
                     Level 8, Suite 8.23/30 Currie Street, Adelaide SA 5000
                  </p>
                  <div className="mt-3 flex items-center gap-3 text-gray-700">
                    <FaPhoneAlt />
                    <a href="tel:+61405141810" className="hover:underline">
                      +61403883559
                    </a>
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-gray-700">
                    <FaEnvelope />
                    <a href="mailto:info@glideedu.com.au" className="hover:underline">
                      info@glideedu.com.au
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-5 overflow-hidden rounded-2xl ring-1 ring-gray-200">
                <div className="relative aspect-video w-full">
                  <iframe
                    title="Adelaide Office Map"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3264.0572348759894!2d138.60491367617156!3d-34.925918372847665!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ab0ceda0c736e0f%3A0x92d9601bc8e1ef1!2s147%20Pirie%20St%2C%20Adelaide%20SA%205000!5e0!3m2!1sen!2sau!4v1731879208311!5m2!1sen!2sau"
                    className="absolute inset-0 h-full w-full"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
